import React, { useState } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import palette from 'src/theme/palette';
import {
  GET_CUSTOM_FORMULAS,
  GET_FACEBOOK_METRICS,
  GET_GOOGLE_ADS_METRICS,
  GET_GOOGLE_ANALYTICS_METRICS,
  GET_SHOPIFY_METRICS,
  GET_MAGENTO_METRICS,
} from 'src/utils/gql/queries';
import {
  ConnectorType,
  MetricsByConnectorType,
  Query,
  ValueType,
  MetricFormatType,
} from 'src/utils/gql/types';

import { useConnectors } from '../../../providers/connectors/hook/useConnectors';
import { BackButton, SaveButton } from '../styled';
import { MetricsMobileContentProps } from '../types';

import { MetricsContent } from './MetricsContent';

export const MetricsMobileContent = ({
  onBackClick,
  onSave,
  connectors,
}: MetricsMobileContentProps) => {
  const snackbar = useSnackbar();

  const [expandedMetricCollapseType, setExpandedMetricCollapseType] = useState<
    (ConnectorType | MetricsByConnectorType)[]
  >([]);

  const toggleMetricCollapse = (
    type: ConnectorType | MetricsByConnectorType,
    isSearch?: boolean,
  ) => {
    setExpandedMetricCollapseType((prevType) =>
      prevType?.includes(type)
        ? prevType.filter((colType) => colType !== type)
        : isSearch
          ? [...prevType, type]
          : [type],
    );
  };

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message = graphQLErrors.length ? graphQLErrors[0].message : errorText;

    snackbar(message);
  };

  const openOrCloseAllMetrics = (isOpen: boolean) => {
    setExpandedMetricCollapseType(
      Object.values(!isOpen ? [] : Object.values(ConnectorType)),
    );
  };

  const { data: facebookMetricsData, loading: gettingFacebookMetrics } =
    useQuery<Pick<Query, 'getFacebookMetrics'>, never>(GET_FACEBOOK_METRICS, {
      onError,
      skip: !connectors?.facebookConnectorId,
    });

  const { data: googleAdsMetricsData, loading: gettingGoogleAdsMetrics } =
    useQuery<Pick<Query, 'getGoogleAdsMetricsList'>, never>(
      GET_GOOGLE_ADS_METRICS,
      { onError, skip: !connectors?.googleAdsConnectorId },
    );

  const {
    data: googleAnalyticsMetricsData,
    loading: gettingGoogleAnalyticsMetrics,
  } = useQuery<Pick<Query, 'getGoogleAnalyticsMetrics'>, never>(
    GET_GOOGLE_ANALYTICS_METRICS,
    { onError, skip: !connectors?.googleAnalytics4ConnectorId },
  );

  const { data: shopifyMetricsData, loading: gettingShopifyMetrics } = useQuery<
    Pick<Query, 'getShopifyMetrics'>,
    never
  >(GET_SHOPIFY_METRICS, { onError, skip: !connectors?.shopifyConnectorId });

  const { data: magentoMetricsData, loading: gettingMagentoMetrics } = useQuery<
    Pick<Query, 'getMagentoMetrics'>,
    never
  >(GET_MAGENTO_METRICS, {
    onError,
    skip:
      !connectors?.magentoConnectorEnabled || !connectors?.magentoConnectorId,
  });

  const { data: customFormulasData, loading: customFormulasLoading } =
    useQuery<Pick<Query, 'getAllCustomFormulas'>>(GET_CUSTOM_FORMULAS);

  const mapFormatTypeToValueType = (
    formatType: MetricFormatType,
  ): ValueType => {
    switch (formatType) {
      case MetricFormatType.Currency:
        return ValueType.currency;
      case MetricFormatType.Percent:
        return ValueType.percentage;
      case MetricFormatType.Duration:
        return ValueType.duration;
      case MetricFormatType.Number:
      default:
        return ValueType.number;
    }
  };

  const transformedCustomFormulaData =
    customFormulasData?.getAllCustomFormulas?.map((el) => {
      return {
        id: el.id,
        label: el.name,
        key: el.id,
        category: 'metric',
        type: MetricsByConnectorType.CUSTOM_FORMULA,
        valueType: mapFormatTypeToValueType(el.format.type),
      };
    });

  const {
    propertyId,
    clientAccountId,
    googleAdsConnectorId,
    googleAnalytics4ConnectorId,
  } = useConnectors();

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
    >
      <MetricsContent
        facebookData={{
          metrics: facebookMetricsData?.getFacebookMetrics.metrics || [],
          loading: gettingFacebookMetrics,
          hasConnector: !!connectors?.facebookConnectorId,
        }}
        shopifyData={{
          metrics: (shopifyMetricsData?.getShopifyMetrics.metrics || []).filter(
            (metric) => metric.label !== 'Customers',
          ),
          loading: gettingShopifyMetrics,
          hasConnector: !!connectors?.shopifyConnectorId,
        }}
        magentoData={{
          metrics: magentoMetricsData?.getMagentoMetrics.metrics || [],
          loading: gettingMagentoMetrics,
          hasConnector: !!connectors?.magentoConnectorId,
        }}
        googleAdsData={{
          metrics:
            googleAdsMetricsData?.getGoogleAdsMetricsList.metrics.filter(
              ({ category }) => category === 'metric',
            ) || [],
          loading: gettingGoogleAdsMetrics,
          hasConnector: !!connectors?.googleAdsConnectorId,
          hasChoosenConnector: !!googleAdsConnectorId && !!clientAccountId,
        }}
        googleAnalyticsData={{
          metrics: (
            googleAnalyticsMetricsData?.getGoogleAnalyticsMetrics.metrics || []
          ).filter(
            (metric) =>
              metric.label !== 'Event name' && metric.key !== 'event_name',
          ),
          loading: gettingGoogleAnalyticsMetrics,
          hasConnector: !!connectors?.googleAnalytics4ConnectorId,
          hasChoosenConnector: !!googleAnalytics4ConnectorId && !!propertyId,
        }}
        kipifyData={{
          metrics: [
            {
              id: 'kipify_total_marketing_spend',
              key: 'total_marketing_spend',
              label: 'Total Marketing Spend',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.currency,
            },
            {
              id: 'kipify_markup',
              key: 'markup',
              label: 'Markup',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.number,
            },
            {
              id: 'kipify_roas',
              key: 'roas',
              label: 'Roas',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.number,
            },
            {
              id: 'kipify_acos',
              key: 'acos',
              label: 'ACOS',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.percentage,
            },
            {
              id: 'kipify_acos_total',
              key: 'acos_total',
              label: 'ACOS (shipping incl.)',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.percentage,
            },
            {
              id: 'kipify_oac',
              key: 'oac',
              label: 'OAC',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.currency,
            },
            {
              id: 'kipify_cac',
              key: 'cac',
              label: 'CAC',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.currency,
            },
            {
              id: 'kipify_cac_new_customers',
              key: 'cac_new_customers',
              label: 'CAC (New Customers)',
              category: 'metric',
              type: MetricsByConnectorType.KIPIFY,
              valueType: ValueType.currency,
            },
          ],
          loading: false,
          hasConnector: true,
        }}
        customFormulaData={{
          metrics: transformedCustomFormulaData || [],
          loading: customFormulasLoading,
          hasConnector: true,
        }}
        expandedCollapseType={expandedMetricCollapseType}
        onToggleCollapse={toggleMetricCollapse}
        openOrCloseAllMetrics={openOrCloseAllMetrics}
      />
      <Box
        display="flex"
        gap="20px"
        borderTop={`1px solid ${palette.grey[100]}`}
        padding="20px"
        alignItems="center"
      >
        <BackButton onClick={onBackClick}>Back</BackButton>
        <SaveButton onClick={onSave}>Save</SaveButton>
      </Box>
    </Box>
  );
};
